import { createContext, useContext, useEffect, useState } from "react";
import { api } from "@/lib/api";

const AppContext = createContext(null);

export const AppProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [cart, setCart] = useState(() => {
    try {
      const raw = JSON.parse(localStorage.getItem("bakery_cart") || "[]");
      // Migrate legacy cart items that don't have cart_key
      return raw.map((i) => ({
        ...i,
        cart_key: i.cart_key || (i.variant_label ? `${i.product_id}::${i.variant_label}` : i.product_id),
      }));
    } catch {
      return [];
    }
  });
  const [cartOpen, setCartOpen] = useState(false);

  useEffect(() => {
    localStorage.setItem("bakery_cart", JSON.stringify(cart));
  }, [cart]);

  useEffect(() => {
    const t = localStorage.getItem("bakery_token");
    if (t) {
      api.get("/auth/me").then((r) => setUser(r.data)).catch(() => {
        localStorage.removeItem("bakery_token");
      });
    }
  }, []);

  const login = async (email, password) => {
    const r = await api.post("/auth/login", { email, password });
    localStorage.setItem("bakery_token", r.data.token);
    setUser(r.data.user);
    return r.data.user;
  };

  const register = async (name, email, password) => {
    const r = await api.post("/auth/register", { name, email, password });
    localStorage.setItem("bakery_token", r.data.token);
    setUser(r.data.user);
    return r.data.user;
  };

  const logout = () => {
    localStorage.removeItem("bakery_token");
    setUser(null);
  };

  const addToCart = (product, qty = 1, variantLabel = null, variantPrice = null) => {
    const price = variantPrice != null ? variantPrice : product.price;
    const displayName = variantLabel ? `${product.name} · ${variantLabel}` : product.name;
    const cartKey = variantLabel ? `${product.id}::${variantLabel}` : product.id;
    setCart((c) => {
      const existing = c.find((i) => i.cart_key === cartKey);
      if (existing) {
        return c.map((i) =>
          i.cart_key === cartKey ? { ...i, quantity: i.quantity + qty } : i
        );
      }
      return [
        ...c,
        {
          cart_key: cartKey,
          product_id: product.id,
          name: displayName,
          price,
          quantity: qty,
          image: product.image,
          variant_label: variantLabel,
        },
      ];
    });
    setCartOpen(true);
  };

  // Bundles: each bundle add creates a new distinct cart line so different
  // pastry selections don't merge with previous bundles.
  const addBundleToCart = (product, selection = []) => {
    const cartKey = `${product.id}::${Date.now()}`;
    setCart((c) => [
      ...c,
      {
        cart_key: cartKey,
        product_id: product.id,
        name: product.name,
        price: product.price,
        quantity: 1,
        image: product.image,
        variant_label: null,
        bundle_selection: selection,
      },
    ]);
    setCartOpen(true);
  };

  const updateQty = (cartKey, qty) => {
    setCart((c) =>
      qty <= 0
        ? c.filter((i) => i.cart_key !== cartKey)
        : c.map((i) => (i.cart_key === cartKey ? { ...i, quantity: qty } : i))
    );
  };

  const removeFromCart = (cartKey) => {
    setCart((c) => c.filter((i) => i.cart_key !== cartKey));
  };

  const clearCart = () => setCart([]);

  const subtotal = cart.reduce((s, i) => s + i.price * i.quantity, 0);
  const cartCount = cart.reduce((s, i) => s + i.quantity, 0);

  return (
    <AppContext.Provider
      value={{
        user, login, register, logout,
        cart, addToCart, addBundleToCart, updateQty, removeFromCart, clearCart,
        cartOpen, setCartOpen, subtotal, cartCount,
      }}
    >
      {children}
    </AppContext.Provider>
  );
};

export const useApp = () => {
  const ctx = useContext(AppContext);
  if (!ctx) throw new Error("useApp must be used inside AppProvider");
  return ctx;
};
